import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';

import {
  Badge,
  Button,
  Card,
  CardHeader,
  ContentLoader,
  EmptyState,
  Input,
  Modal,
  ModalFooter,
  Select,
  getStatusBadgeVariant,
} from '@/components/common';
import { getErrorMessage } from '@/services';
import { brokerMappingRequestsApi } from '@/services/pospPortal';
import { formatDate, titleCase } from '@/utils';

/**
 * P1 — "Map to My Broker", built as a REQUEST rather than a switch.
 *
 * The ask was a control on this page letting an agent map themselves to a
 * broker. `POSP.broker` is not a preference though — it is the tenant key for
 * the agent's policies, quotes, customers, commissions, payouts and targets, so
 * a self-service dropdown would let one agent move (or strand) a brokerage's
 * business with one click and nobody's consent.
 *
 * So the agent ASKS and the receiving brokerage answers, on the same
 * request/approve/decline shape the platform already uses for a broker asking
 * to be connected to an insurer (`BrokerInsurerConnectionRequest`). This card
 * is the agent's half: file one, see where it stands, withdraw it. Nothing here
 * changes the agent's broker — the API will not let it.
 */

/**
 * M-03 — "who do I sell for?", answered unconditionally.
 *
 * `currentBrokerName` was already a prop, but it was rendered ONLY inside the
 * EmptyState below, so the moment an agent had any request at all — pending,
 * approved, declined — the one fact this screen is named after disappeared.
 * It is a fact about the AGENT, not about their request list, so it is drawn
 * here: above the list, before it has loaded, and whether or not it loads.
 */
function CurrentBrokerage({
  name,
  code,
  logoUrl,
}: {
  name?: string;
  code?: string;
  logoUrl?: string;
}) {
  return (
    <div className="mb-4 flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white dark:bg-gray-900">
        {logoUrl ? (
          <img src={logoUrl} alt={`${name} logo`} className="h-full w-full object-contain" />
        ) : (
          <BuildingOffice2Icon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
        )}
      </div>
      <div className="min-w-0">
        {name ? (
          <>
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">You currently sell for</p>
            <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{name}</p>
            {code && <p className="text-xs text-gray-500 dark:text-gray-400">{code}</p>}
          </>
        ) : (
          <>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              You are not mapped to a brokerage yet
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Until a brokerage takes you on, you cannot sell a policy.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export function PospBrokerMappingCard({
  currentBrokerName,
  currentBrokerCode,
  currentBrokerLogoUrl,
}: {
  currentBrokerName?: string;
  currentBrokerCode?: string;
  currentBrokerLogoUrl?: string;
}) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [broker, setBroker] = useState('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState('');

  const requestsQuery = useQuery({
    queryKey: ['posp-broker-mapping-requests'],
    queryFn: () => brokerMappingRequestsApi.list(),
  });

  // Only fetched once the agent opens the form: an agent who never asks should
  // not pull a broker list on every visit to their profile.
  const brokersQuery = useQuery({
    queryKey: ['posp-broker-mapping-choices'],
    queryFn: () => brokerMappingRequestsApi.brokers(),
    enabled: showForm,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['posp-broker-mapping-requests'] });

  const createMutation = useMutation({
    mutationFn: () => brokerMappingRequestsApi.create(broker, note),
    onSuccess: () => {
      setShowForm(false);
      setBroker('');
      setNote('');
      setFormError('');
      invalidate();
    },
    onError: (err) => setFormError(getErrorMessage(err)),
  });

  const withdrawMutation = useMutation({
    mutationFn: (id: string) => brokerMappingRequestsApi.withdraw(id),
    onSuccess: invalidate,
    onError: (err) => setFormError(getErrorMessage(err)),
  });

  const requests = requestsQuery.data?.results ?? [];
  // At most one may be open at a time; the API enforces it with a partial
  // unique constraint, and the form is disabled here so the agent is not
  // offered a button that would 400.
  const openRequest = requests.find((row) => row.status === 'pending');

  function renderBody() {
    if (requestsQuery.isLoading) {
      return <ContentLoader />;
    }

    if (requestsQuery.isError) {
      return (
        <div className="text-center py-8">
          <p className="text-sm font-medium text-gray-900 dark:text-white">Your requests could not be loaded</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{getErrorMessage(requestsQuery.error)}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => requestsQuery.refetch()}>
            Try again
          </Button>
        </div>
      );
    }

    if (requests.length === 0) {
      return (
        <EmptyState
          icon={<BuildingOffice2Icon />}
          title="No mapping requests"
          // The brokerage itself is named once, in the banner above — repeating
          // it here was the same fact twice on one card.
          description={
            currentBrokerName
              ? 'To move to another brokerage, ask them — they decide.'
              : 'To be mapped to a brokerage, send them a request. They decide.'
          }
        />
      );
    }

    return (
      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        {requests.map((row) => (
          <li key={row.id} className="py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{row.to_broker_name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Sent {formatDate(row.created_at)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant={getStatusBadgeVariant(row.status)}>{titleCase(row.status)}</Badge>
                {row.status === 'pending' && (
                  <Button
                    variant="outline"
                    size="sm"
                    isLoading={withdrawMutation.isPending && withdrawMutation.variables === row.id}
                    onClick={() => withdrawMutation.mutate(row.id)}
                  >
                    Withdraw
                  </Button>
                )}
              </div>
            </div>
            {row.status === 'declined' && row.decline_reason && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{row.decline_reason}</p>
            )}
            {/* An approval that could not move the agent says exactly what
                stopped it, rather than leaving them to guess why the broker on
                their profile did not change. */}
            {row.status === 'approved' && !row.applied && row.blocked_reason && (
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">{row.blocked_reason}</p>
            )}
            {row.status === 'approved' && row.applied && (
              <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                You are now mapped to {row.to_broker_name}. They will activate your account.
              </p>
            )}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Map to a Broker"
        subtitle="Ask a brokerage to take you on. They approve it — your broker never changes on your say-so alone."
        action={
          <Button size="sm" disabled={Boolean(openRequest)} onClick={() => setShowForm(true)}>
            New request
          </Button>
        }
      />

      <CurrentBrokerage
        name={currentBrokerName}
        code={currentBrokerCode}
        logoUrl={currentBrokerLogoUrl}
      />

      {openRequest && (
        <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
          {openRequest.to_broker_name} has not answered yet. Withdraw that request before sending another.
        </p>
      )}

      {renderBody()}

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Ask to be mapped to a broker">
        <div className="space-y-4">
          {brokersQuery.isLoading && <ContentLoader />}
          {brokersQuery.isError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              The list of brokerages could not be loaded. {getErrorMessage(brokersQuery.error)}
            </p>
          )}
          {brokersQuery.isSuccess && brokersQuery.data.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              There is no other active brokerage to ask in your country.
            </p>
          )}
          {brokersQuery.isSuccess && brokersQuery.data.length > 0 && (
            <Select
              id="mapping-broker"
              name="mapping_broker"
              label="Brokerage"
              placeholder="Choose a brokerage"
              value={broker}
              onChange={(event) => setBroker(event.target.value)}
              options={brokersQuery.data.map((row) => ({
                value: row.id,
                label: `${row.name} (${row.code})`,
              }))}
            />
          )}
          <Input
            id="mapping-note"
            name="mapping_note"
            label="Message to the brokerage"
            placeholder="Why you would like to sell for them"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          {formError && <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>}
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowForm(false)}>
            Cancel
          </Button>
          <Button
            disabled={!broker}
            isLoading={createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Send request
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}
